import React, { useState, useRef } from 'react';
import Header from '../components/Header';
import house from "../assets/house.png";
import ContactUs from '../components/ContactUs';
import './Home.css';
import Carousel from '../components/Carousel';
import WhereWeOperate from '../components/WhereWeOperate';
import Footer from '../components/Footer';
import Airbnbservice from '../components/Airbnbservice';
import Reviews from '../components/Reviews';
import CaseStudy from '../components/CaseStudy';
import BookingBanner from '../components/BookingBanner';
import Host from "../assets/host.png";
import { Helmet } from 'react-helmet';
import PriceCardBnb from '../components/PriceCardBnb';

const Home = () => {
  const [showPopupOnClick, setShowPopupOnClick] = useState(false);
  const priceRef = useRef(null);
  const serviceRef = useRef(null);
  const caseStudyRef = useRef(null);

  const scrollToServices = () => {
    serviceRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToCaseStudy = () => {
    caseStudyRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToPrice = () => {
    priceRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const popupStyleButton = {
    position: 'fixed',
    top: '35%',
    left: '50%',
    height: '60%',
    transform: showPopupOnClick ? 'translate(-50%, -50%)' : 'translate(-50%, -150%)',
    backgroundColor: 'black',
    display: showPopupOnClick ? 'block' : 'none',
    zIndex: 9999,
    borderRadius: '15px',
  };

  const handleClose = () => {
    setShowPopupOnClick(false);
  };

  // ----- JSON-LD Schemas -----
  const localBusinessLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "AirBooked – Airbnb Property Management Melbourne",
    image: "https://airbooked.com.au/og-cover.jpg",
    url: "https://airbooked.com.au/",
    telephone: "+61 433 310 964",
    email: "sales@airbooked.com.au",
    priceRange: "16% Management",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Melbourne",
      addressRegion: "VIC",
      addressCountry: "AU",
    },
    areaServed: [
      { "@type": "City", name: "Melbourne" },
      { "@type": "AdministrativeArea", name: "Mornington Peninsula" },
      { "@type": "AdministrativeArea", name: "Great Ocean Road" },
      { "@type": "AdministrativeArea", name: "Country Victoria" },
    ],
    sameAs: [
      "https://www.facebook.com/profile.php?id=61569044886744",
      "https://www.instagram.com/shaunvan_airbooked/",
      "https://www.linkedin.com/in/shaun-van-amersfoort-80b7b7194/",
    ],
    founder: { "@type": "Person", name: "Shaun Van Amersfoort" },
  };

  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "360 Airbnb Property Management",
    brand: "AirBooked",
    description:
      "Full 360 management service: cleaning, linen, maintenance, guest comms, pricing, and marketing.",
    url: "https://airbooked.com.au/",
    offers: {
      "@type": "Offer",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        priceCurrency: "AUD",
        name: "Management fee",
        price: "0.16",
      },
      priceCurrency: "AUD",
      availability: "https://schema.org/InStock",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "25",
    },
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is your management fee?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Our standard management fee starts from 16% including guest support, pricing, and operations.",
        },
      },
      {
        "@type": "Question",
        name: "Do you service inner-north Melbourne suburbs?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Yes—Brunswick, Coburg, Fitzroy, Essendon, Pascoe Vale, Glenroy and more.",
        },
      },
      {
        "@type": "Question",
        name: "Can you manage cleaning and linen?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Yes, we handle full turnover cleaning and linen with quality checks and restocking.",
        },
      },
    ],
  };

  return (
    <div className='main-body-home'>
      <Helmet>
        <title>Airbnb Property Management Melbourne | AirBooked</title>
        <link rel="canonical" href="https://airbooked.com.au/" />
        <meta
          name="description"
          content="Airbnb & holiday rental property management in Melbourne. Cleaning, linen, maintenance, pricing, guest support & marketing. Transparent management fee."
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Airbnb Property Management Melbourne | AirBooked" />
        <meta
          property="og:description"
          content="Full-service short-term rental management in Melbourne. Cleaning, linen, maintenance, and guest support included."
        />
        <meta property="og:url" content="https://airbooked.com.au/" />
        <meta property="og:image" content="https://airbooked.com.au/og-cover.jpg" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Airbnb Property Management Melbourne | AirBooked" />
        <meta name="twitter:description" content="Melbourne’s end-to-end Airbnb property management." />
        <meta name="twitter:image" content="https://airbooked.com.au/og-cover.jpg" />

        {/* ✅ JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify(localBusinessLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(productLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqLd)}
        </script>
      </Helmet>

      <div className="popup" style={popupStyleButton}>
        <button className="close-button" onClick={handleClose}>X</button>
        <ContactUs />
      </div>

      <div className='header-main'>
        <Header
          onServicesClick={scrollToServices}
          onCaseStudyClick={scrollToCaseStudy}
          onPriceClick={scrollToPrice}
        />
      </div>

      <div className='home-top'>
        <img
          className="house"
          src={house}
          alt="Modern Melbourne Airbnb-ready house exterior"
          width="1600"
          height="900"
          fetchpriority="high"
        />

        <div className='des-headers'>
          <h1 className='main-head'>Airbnb Property Management in Melbourne</h1>
          <h2 className='sec-head'>
            AirBooked provides end-to-end property management service,
            from cleaning, linen change, maintenance, fitouts, styling and marketing.
            <br />No contract <strong>16% management</strong>
          </h2>

          <button className="book-chat-top" onClick={() => setShowPopupOnClick(true)}>
            Get a info pack
          </button>
        </div>
      </div>

      <div className="property-management-container">
        <img className="host" src={Host} alt="host" />
        <p className="property-host">Your dedicated property manager | Shaun Van Amersfoort</p>
        <h3 className="heading-prop">
          Lets us take care of the operational headache<br /> of running your airbnb.
        </h3>
        <p className="body-text-prop">
          We’re vacation home experts offering premium Short term property management and cleaning services across Australia.
          With our expertise, we ensure your property shines in the market and deliver seamless, professional management
          from start to finish.
        </p>
        <p className="body-text-prop">
          Our dedication goes beyond property owners – we also take pride in providing exceptional guest services, ensuring
          every guest feels valued and well cared for.
        </p>
        <p className="body-text-prop">
          From beginning to end, we’re here to support both you and your guests at every stage.
        </p>
      </div>

      <div className='caro-container'>
        <Carousel />
      </div>

      <BookingBanner />

      <div className="property-management-container">
        <h3 className="heading-prop">Maximize Your Short-Term <br />Rental Success with AirBooked.</h3>
        <p className="body-text-prop">
          At AirBooked, we specialize in transforming traditional long-term rentals into premium short-term vacation stays.
          Our full-service property management approach helps you boost your rental income, reduce the time spent searching
          for guests, and increase overall profitability.
        </p>
        <p className="body-text-prop">
          With our expert team handling every detail, you’ll enjoy more free time and peace of mind.
        </p>
      </div>

      <div ref={caseStudyRef}>
        <CaseStudy />
      </div>

      <div className="service-section" ref={serviceRef}>
        <Airbnbservice />
      </div>

      <div ref={priceRef}>
        <PriceCardBnb />
      </div>

      <Reviews />
      <WhereWeOperate />
      <Footer />
    </div>
  );
};

export default Home;
